import React, { useState, useEffect } from 'react';
import { FilledButton } from '@ds/react-components';
import { useParams, useHistory } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { putSuspension as updateSuspension } from 'services/suspensions';
import {
  setSuspensionAlertMessage,
  getSuspensionFormData,
} from 'utils/suspensions';
import * as moment from 'moment';
import SuspensionChargeOptionForEdit from '../common/SuspensionChargeOption/SuspensionChargeOptionForEdit';
import SuspensionReasonTextarea from '../common/SuspensionReasonTextarea';
import SuspensionDateRangePicker from '../common/SuspensionDateRangePicker';
import SuspensionFormCancelButton from '../common/SuspensionFormCancelButton';

const EditSuspensionForm = (props) => {
  const {
    setErrorMsg,
    updateAppLoading,
    suspensionInfo,
    localisation,
    currentSuspension,
    canChargeForSuspension,
    handleSelectStartDate,
  } = props;
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { adfitNo, scheduleSuspenseId } = useParams();
  const history = useHistory();
  const { remainingSuspensionPeriod, allowedSuspensionPeriod } = suspensionInfo;
  const {
    handleSubmit,
    register,
    control,
    setValue,
    errors,
    getValues,
    reset,
  } = useForm();

  //set up form default values
  useEffect(() => {
    reset({
      ...currentSuspension,
      suspensionStartDate:
        currentSuspension.suspensionStartDate &&
        moment(currentSuspension.suspensionStartDate),
      suspensionEndDate:
        currentSuspension.suspensionEndDate &&
        moment(currentSuspension.suspensionEndDate),
    });
  }, [currentSuspension, reset]);

  const watchSuspensionStartDate = useWatch({
    control,
    name: 'suspensionStartDate',
  });

  //Submit the form
  const onSubmitHandler = (data) => {
    const formData = getSuspensionFormData(data, currentSuspension);
    setIsSubmittingForm(true);
    updateAppLoading(true);
    updateSuspension(adfitNo, scheduleSuspenseId, formData)
      .then((res) => {
        updateAppLoading(false);
        const suspensionAlertMessage = {
          variant: 'success',
          title: 'Suspension updated successfully.',
          response: res,
        };
        setSuspensionAlertMessage(suspensionAlertMessage);
        history.push(`/Customers/${adfitNo}/Payments/Suspensions`);
      })
      .catch((err) => {
        setIsSubmittingForm(false);
        updateAppLoading(false);
        const result = err.response.json();
        result
          .then((error) => {
            setErrorMsg(error);
          })
          .catch((error) => {
            setErrorMsg(error);
          });
      });
  };

  const editableSuspensionPeriod =
    (moment(currentSuspension.suspensionEndDate).diff(
      moment(currentSuspension.suspensionStartDate),
      'days'
    ) || 0) + (remainingSuspensionPeriod > 0 ? remainingSuspensionPeriod : 0);
  const canSuspendDays =
    editableSuspensionPeriod > allowedSuspensionPeriod * 7
      ? allowedSuspensionPeriod * 7
      : editableSuspensionPeriod;

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <SuspensionDateRangePicker
        disableStartDate={!currentSuspension.isFuture}
        minEndDate={
          currentSuspension.isFuture
            ? moment(watchSuspensionStartDate)
            : moment()
        }
        /*When remainingSuspensionPeriod is not greater than 0, no limitation for end date calendar, get the validation message from API*/
        maxEndDate={
          remainingSuspensionPeriod > 0
            ? moment(watchSuspensionStartDate).add(canSuspendDays, 'days')
            : undefined
        }
        {...{
          canChargeForSuspension,
          allowedSuspensionPeriod,
          localisation,
          control,
          setValue,
          getValues,
          errors,
          handleSelectStartDate,
        }}
      />
      <SuspensionChargeOptionForEdit
        {...{
          register,
          control,
          setValue,
          canChargeForSuspension,
          currentSuspension,
          suspensionInfo,
          errors,
        }}
      />
      <SuspensionReasonTextarea register={register} />
      <FilledButton variant="primary" isDisabled={isSubmittingForm}>
        Save
      </FilledButton>
      <SuspensionFormCancelButton isDisabled={isSubmittingForm} />
    </form>
  );
};

export default EditSuspensionForm;
